<rt-box layout-vertical [formGroup]="$any(openingsFormArray)" [class.invalid]="isInvalid">
  <div margin-bottom>
    <rt-heading variant="heading large block">
      {{ 'app.requisitions.details_page.openings.heading' | translate }}
      <span [class.show-asterisk]="showAsterisk" class="asterisk">*</span>
      <rt-badge
        color="counter-default"
        [text]="openingsCounter"
        [rtTooltip]="'app.requisitions.openings.counter_tooltip' | translate"
        margin-left-small-x
      />
    </rt-heading>
    <rt-heading variant="subheading normal">
      {{ 'app.requisitions.details_page.openings.subheading_all' | translate }}
    </rt-heading>
  </div>
  @if (showReduceNumberOfOpeningsInfobox) {
    <rt-infobox
      [heading]="'app.requisitions.openings.reduce_openings_infobox.heading' | translate"
      iconName="info-outline"
      type="transparent"
      [isDismissible]="false"
      margin-bottom
    >
      {{ 'app.requisitions.openings.reduce_openings_infobox.description' | translate }}
    </rt-infobox>
  }
  @if (
    canDeleteOpeningsAndUnassign && openingsRequireOnlyNumber && deletedOpeningsCandidatesCount
  ) {
    <rt-infobox
      [heading]="'app.requisitions.openings.candidates_deleted_infobox.title' | translate"
      iconName="error"
      type="error"
      [isDismissible]="false"
      margin-bottom
    >
      {{ 'app.requisitions.openings.candidates_deleted_infobox.description_1' | translate }}
      <a routerLink="../hires" text-capitalize>{{ 'app.requisitions.tabs.hires' | translate }}</a>
      {{ 'app.requisitions.openings.candidates_deleted_infobox.description_2' | translate }}
    </rt-infobox>
  }
  @if (!isFormEditable && !savedOpenings.length) {
    <rt-infobox iconName="info-outline" type="transparent" [isDismissible]="false">
      {{ 'app.requisitions.openings.no_openings_yet_infobox.description' | translate }}
    </rt-infobox>
  }

  <div class="openings-wrap-grid">
    @if (openingsRequireOnlyNumber) {
      <div class="number-of-openings">
        <rt-form-field [hasLabelMargin]="false">
          <rt-label margin-bottom-small-xx>
            {{ 'app.offer_form.number_of_openings' | translate }}
          </rt-label>
          @if (!canOnlyDecreaseOpenings) {
            <rt-input
              [placeholder]="'app.offer_form.number_of_openings_placeholder' | translate"
              [readOnly]="!isFormEditable"
              [formControl]="numberOfOpeningsControl"
              [rtFormError]="{ max: 'app.offer_form.errors.number_of_openings' | translate }"
              [required]="!numberOfOpeningsControl.value"
            />
          }
          @if (canOnlyDecreaseOpenings) {
            <rt-select
              [hasInputTrigger]="true"
              [options]="decreaseOpeningsSelectOptions"
              [includeEmptyOption]="false"
              [hasSearch]="false"
              [formControl]="numberOfOpeningsControl"
              [hideSelectedOption]="true"
              [readOnly]="!canDeleteOpeningsAndUnassign || openingsFormArray.length === 1"
            />
          }
        </rt-form-field>
      </div>
    } @else {
      @for (
        opening of openingsFormArray.controls;
        track trackByIndex(index, opening);
        let index = $index;
        let openingsCount = $count
      ) {
        <div #openings margin-bottom>
          @if (selectedOpeningIndex !== index) {
            <rt-box
              padding-right-small
              padding-tb-small
              layout
              layout-align-items-center
              layout-space-between
              [class.disabled]="selectedOpeningIndex !== null"
              variant="no-padding"
            >
              <div layout layout-align-items-center>
                <rt-heading variant="heading lighter" margin-left>
                  <ng-container
                    [ngTemplateOutlet]="openingInfoTpl"
                    [ngTemplateOutletContext]="{ opening }"
                  />
                </rt-heading>

                @if (opening.get('status')?.value === RequisitionOpeningStatus.Filled) {
                  <rt-badge
                    color="secondary"
                    [text]="'app.requisitions.status.filled' | translate"
                    [iconName]="'hired'"
                    margin-left-small-x
                  />
                }
              </div>

              <div layout layout-align-items-center>
                @if (isFormEditable) {
                  <rt-icon-button
                    (click)="copyOpening(index)"
                    variant="plain"
                    iconName="content-copy"
                    shape="square"
                  />
                }
                @if (isFormEditable) {
                  <rt-icon-button
                    (click)="editOpening(index)"
                    variant="plain"
                    iconName="edit"
                    shape="square"
                  />
                }
                @if (
                  canOnlyDecreaseOpenings &&
                  openingsCount > 1 &&
                  opening.get('status')?.value !== RequisitionOpeningStatus.Filled &&
                  canDeleteOpeningsAndUnassign
                ) {
                  <rt-icon-button
                    (click)="deleteOpening(index, false)"
                    variant="plain"
                    iconName="delete"
                    shape="square"
                  />
                }

                @if (
                  opening.get('status')?.value === RequisitionOpeningStatus.Filled &&
                  deletedOpeningsCandidatesCount &&
                  canDeleteOpeningsAndUnassign
                ) {
                  <ng-container
                    [ngTemplateOutlet]="unassignCandidateButtonTpl"
                    [ngTemplateOutletContext]="
                      getPossibleDeletedCandidateData(opening.get('id')?.value!)
                    "
                  />
                }
              </div>
            </rt-box>
          }

          @if (selectedOpeningIndex === index) {
            <rt-box variant="background-light-blue no-padding" [formGroupName]="index">
              <div layout layout-vertical layout-full-width>
                <div layout padding class="opening-editor-fields">
                  @if (availableOpeningsFields['reason']) {
                    <rt-form-field [required]="!opening.get('reason')?.value">
                      <rt-label>
                        {{ 'app.requisitions.form_fields.reason' | translate }}
                      </rt-label>
                      <rt-select
                        [hasInputTrigger]="true"
                        [options]="openingReasonSelectOptions"
                        [includeEmptyOption]="false"
                        [hasSearch]="false"
                        formControlName="reason"
                      />
                    </rt-form-field>
                  }
                  @if (availableOpeningsFields['anticipated_start_date']) {
                    <rt-form-field [required]="!opening.get('anticipatedStartDate')?.value">
                      <rt-label>
                        {{ 'app.requisitions.form_fields.anticipated_start_date' | translate }}
                      </rt-label>
                      <rt-input
                        #anticipatedStartDateDropdownOrigin="rtOverlayOrigin"
                        [hasDropdownIndicator]="true"
                        [readOnly]="true"
                        [placeholder]="'app.common.select' | translate"
                        [value]="
                          displayAnticipatedStartDate(opening.get('anticipatedStartDate')?.value!)
                        "
                        [class.dropdownOpened]="anticipatedStartDatePickerOpen"
                        (click)="toggleStartDatePicker()"
                        variant="rounded"
                        rt-overlay-origin
                      />
                      <ng-template
                        #anticipatedStartDateDropdown="rtConnectedOverlay"
                        [origin]="anticipatedStartDateDropdownOrigin"
                        [open]="anticipatedStartDatePickerOpen"
                        (backdropClick)="toggleStartDatePicker()"
                        rt-connected-overlay
                        hasBackdrop
                      >
                        <rt-dropdown (click)="$event.preventDefault()">
                          <rt-date-picker
                            direction="future"
                            [value]="opening.get('anticipatedStartDate')?.value!"
                            [selectNowByDefault]="false"
                            formControlName="anticipatedStartDate"
                            (valueChange)="toggleStartDatePicker()"
                          />
                        </rt-dropdown>
                      </ng-template>
                    </rt-form-field>
                  }
                  @if (availableOpeningsFields['explanation']) {
                    <rt-form-field [required]="!opening.get('explanation')?.value">
                      <rt-label>
                        {{ 'app.requisitions.form_fields.explanation' | translate }}
                      </rt-label>
                      <rt-input [maxLength]="255" formControlName="explanation" />
                    </rt-form-field>
                  }
                </div>
                <rt-divider />

                <div layout layout-space-between padding-medium>
                  <rt-button
                    (click)="deleteOpening(index)"
                    variant="plain"
                    class="delete danger-on-hover"
                  >
                    <rt-icon name="delete" />
                    <span>{{ 'app.common.delete' | translate }}</span>
                  </rt-button>
                  <rt-group>
                    <rt-button (click)="cancelOpening(index)" variant="plain">
                      {{ 'app.common.cancel' | translate }}
                    </rt-button>
                    <rt-button
                      [disabled]="opening.invalid"
                      (click)="saveOpening(index, true)"
                      variant="outline"
                      margin-left-small
                    >
                      {{ 'app.common.save_and_add_another' | translate }}
                    </rt-button>
                    <rt-button
                      [disabled]="opening.invalid"
                      (click)="saveOpening(index, false)"
                      variant="primary"
                      margin-left-small
                    >
                      {{ 'app.common.save' | translate }}
                    </rt-button>
                  </rt-group>
                </div>
              </div>
            </rt-box>
          }
        </div>
      }
      @if (isFormEditable) {
        <rt-add-new-button
          variant="wide"
          [disabled]="selectedOpeningIndex !== null"
          [text]="'app.common.add_new' | translate"
          (click)="addNewOpening()"
        />
      }
    }
  </div>
</rt-box>

<ng-template #openingInfoTpl let-opening="opening">
  <div class="opening-info-wrapper">
    @if (opening.get('anticipatedStartDate')?.value) {
      <rt-icon [name]="'calendar-today'" colorToken="colorIconDefault" margin-right-small-x />
      {{ displayAnticipatedStartDate(opening.get('anticipatedStartDate')?.value!) }}
      @if (opening.get('reason')?.value || opening.get('explanation')?.value) {
        <rt-divider [variant]="'dot'" />
      }
    }

    @if (opening.get('reason')?.value) {
      {{ displayOpeningReason(opening.get('reason')?.value!) }}
      @if (opening.get('explanation')?.value) {
        <rt-divider [variant]="'dot'" />
      }
    }

    @if (opening.get('explanation')?.value) {
      <rt-truncate
        [rtTooltip]="opening.get('explanation')?.value"
        [rtTooltipMaxWidth]="450"
        class="explanation"
      >
        {{ opening.get('explanation')?.value }}
      </rt-truncate>
    }
    @if (opening.invalid) {
      <rt-truncate>
        <div class="error" margin-left-small-x>
          <rt-icon name="error-outline" [colorToken]="'colorIconCriticalDefault'" />
          {{ getOpeningErrorText(opening) }}
        </div>
      </rt-truncate>
    }
  </div>
</ng-template>

<ng-template
  #unassignCandidateButtonTpl
  let-isRecentlyDeleted="isRecentlyDeleted"
  let-isDeleted="isDeleted"
  let-openingId="openingId"
>
  @if (isDeleted || isRecentlyDeleted) {
    <div
      [rtTooltip]="
        isRecentlyDeleted
          ? ('app.requisitions.openings.candidate_recently_deleted_tooltip' | translate)
          : ('app.requisitions.openings.candidate_deleted_tooltip' | translate)
      "
      [rtTooltipHasTextAlignCenter]="true"
    >
      <rt-button
        variant="plain"
        [disabled]="isRecentlyDeleted"
        (click)="unassignCandidate.emit(openingId)"
      >
        <rt-icon name="block" />
        {{ 'app.requisitions.openings.unassign_from_opening' | translate }}
      </rt-button>
    </div>
  }
</ng-template>
